import type InlineQuestionResponse from '../../../types/InlineQuestionResponse';
import type YellowLightCategoryType from '../../../types/YellowLightCategoryType';
import type { PreferenceType } from '../../PreferencesCollection/utils/usePreferenceTiles';
import type { PrimaryHeadingProps } from '../PrimaryHeading';

import { Flex, Heading, type IconProps } from '../../../../../../../../mads';
import { InlineQuestion } from '../InlineQuestion';
import { TileList, type TileListProps } from '../TileList';

import { colors } from '@indeed/ifl-tokens';
import React from 'react';

export type TileSectionSentimentCounts = {
    total: number;
    positive: number;
    negative: number;
    unknown: number;
};

export interface TileSectionProps {
    title: string;
    type: YellowLightCategoryType | PreferenceType;
    tiles: TileListProps['tiles'];
    icon?: React.FC<IconProps>;
    inlineQuestionLabel?: string;
    onSubmit?: (response: InlineQuestionResponse) => void;
    subtitleProps?: PrimaryHeadingProps['subtitleProps'];
    isOpen?: boolean;
    isTruncatable?: boolean;
    sentimentCounts?: TileSectionSentimentCounts;
}

export const TileSection: React.FunctionComponent<
    TileSectionProps & {
        children?: React.ReactNode;
    }
> = ({ title, tiles = [], icon: Icon, type, onSubmit, inlineQuestionLabel, children }) => {
    return (
        <Flex role="group" sx={{ gap: 24, flexDirection: 'row' }} aria-label={title}>
            {Icon && (
                <Icon
                    data-testid="section-icon"
                    size="sm"
                    aria-hidden={true}
                    color={colors.neutral[700]}
                />
            )}
            <Flex sx={{ gap: 8, flex: 1 }}>
                {title && (
                    <Heading level={2} sx={{ marginBottom: 0 }}>
                        {title}
                    </Heading>
                )}
                {tiles.length > 0 && <TileList tiles={tiles} section={type} />}
                {onSubmit && inlineQuestionLabel && (
                    <InlineQuestion
                        label={inlineQuestionLabel}
                        onSubmit={(response) => {
                            onSubmit(response);
                        }}
                    />
                )}
                {children}
            </Flex>
        </Flex>
    );
};
